/**
 * Cursor-based PDF layout engine for Advance Ledger reports.
 * Calculates remaining page height before each block and prevents orphan section headers.
 */
import autoTable from 'jspdf-autotable';
import { ledgerMargins } from './shared';

export const TABLE_METRICS = {
  SECTION_HEADER: 8,
  TABLE_HEAD: 9,
  ROW_DEFAULT: 7,
  ROW_COMPACT: 6.5,
  GAP_AFTER_SECTION: 6,
  GAP_AFTER_TABLE: 6,
  GAP_AFTER_TEXT: 4,
  LINE_HEIGHT: 4,
  SIGNOFF_BLOCK: 32,
  KPI_BLOCK: 32,
};

const defaultDebug = () => {
  try {
    return process.env.NODE_ENV !== 'production';
  } catch {
    return false;
  }
};

export class PdfLayoutEngine {
  /**
   * @param {import('jspdf').jsPDF} doc
   * @param {object} layout - LEDGER_LAYOUT | PREMIUM_LAYOUT
   * @param {{ debug?: boolean, label?: string }} options
   */
  constructor(doc, layout, options = {}) {
    this.doc = doc;
    this.layout = layout;
    this.debug = options.debug ?? defaultDebug();
    this.label = options.label || 'ledger-pdf';
    this.cursorY = layout.FIRST_PAGE_START;
  }

  get pageHeight() {
    return this.doc.internal.pageSize.getHeight();
  }

  get pageBottom() {
    return this.pageHeight - this.layout.BOTTOM;
  }

  get remainingHeight() {
    return Math.max(0, this.pageBottom - this.cursorY);
  }

  get currentPage() {
    const info = this.doc.internal.getCurrentPageInfo?.();
    return info?.pageNumber ?? this.doc.internal.getNumberOfPages();
  }

  log(pageBreakReason, extra = {}) {
    if (!this.debug) return;
    console.debug('[PdfLayoutEngine]', {
      report: this.label,
      page: this.currentPage,
      cursorY: Number(this.cursorY.toFixed(2)),
      remainingHeight: Number(this.remainingHeight.toFixed(2)),
      pageBreakReason,
      ...extra,
    });
  }

  /** Set cursor explicitly (e.g. after external draw helper returns end Y). */
  setCursor(y) {
    this.cursorY = y;
    return this.cursorY;
  }

  advance(mm) {
    this.cursorY += mm;
    return this.cursorY;
  }

  /**
   * Minimum height to keep section title + table header + first N body rows together.
   */
  estimateSectionTableKeepTogether(rowCount, {
    rowHeight = TABLE_METRICS.ROW_DEFAULT,
    minRows = 2,
    withSectionHeader = true,
    sectionHeaderHeight = TABLE_METRICS.SECTION_HEADER,
  } = {}) {
    const rows = Math.max(0, Math.min(rowCount, minRows));
    const section = withSectionHeader ? sectionHeaderHeight : 0;
    return section + TABLE_METRICS.TABLE_HEAD + rows * rowHeight;
  }

  /** Estimate full table height (for small tables only — not used for page breaks on large tables). */
  estimateTableHeight(rowCount, {
    rowHeight = TABLE_METRICS.ROW_DEFAULT,
    withSectionHeader = false,
  } = {}) {
    const section = withSectionHeader ? TABLE_METRICS.SECTION_HEADER : 0;
    return section + TABLE_METRICS.TABLE_HEAD + rowCount * rowHeight;
  }

  /**
   * New page only when the required block cannot fit in remaining space.
   */
  ensureSpace(requiredMm, pageBreakReason, extra = {}) {
    const sectionHeight = requiredMm;
    if (sectionHeight > this.remainingHeight) {
      this.log(pageBreakReason, {
        sectionHeight: Number(sectionHeight.toFixed(2)),
        needed: Number(sectionHeight.toFixed(2)),
        action: 'addPage',
        ...extra,
      });
      this.doc.addPage();
      this.cursorY = this.layout.TOP;
    } else {
      this.log(`${pageBreakReason}:fits`, {
        sectionHeight: Number(sectionHeight.toFixed(2)),
        action: 'continue',
        ...extra,
      });
    }
    return this.cursorY;
  }

  /**
   * Draw free-form block only if it fits; otherwise break first.
   */
  drawBlock(requiredMm, pageBreakReason, drawFn) {
    this.ensureSpace(requiredMm, pageBreakReason);
    const startY = this.cursorY;
    this.cursorY = drawFn(this.doc, startY) ?? startY;
    return this.cursorY;
  }

  drawSectionTitle(num, title, drawTitleFn) {
    this.cursorY = drawTitleFn(this.doc, this.cursorY, num, title);
    return this.cursorY;
  }

  /**
   * Section title + autoTable with keep-together guard (no orphan headers).
   * Long tables split across pages; only the opening block is kept together.
   */
  drawSectionTable({
    sectionId,
    sectionNum,
    sectionTitle,
    drawTitleFn,
    rowCount = 0,
    rowHeight = TABLE_METRICS.ROW_DEFAULT,
    minKeepRows = 2,
    sectionHeaderHeight = TABLE_METRICS.SECTION_HEADER,
    tableOptions,
    afterGap = TABLE_METRICS.GAP_AFTER_TABLE,
  }) {
    const keepTogether = this.estimateSectionTableKeepTogether(rowCount, {
      rowHeight,
      minRows: minKeepRows,
      withSectionHeader: Boolean(drawTitleFn || sectionTitle),
      sectionHeaderHeight,
    });

    this.ensureSpace(keepTogether, `section-keep-together:${sectionId}`, {
      sectionId,
      keepTogether: Number(keepTogether.toFixed(2)),
      rowCount,
    });

    if (drawTitleFn) {
      this.cursorY = drawTitleFn(this.doc, this.cursorY);
    }

    const tableStartY = this.cursorY;

    autoTable(this.doc, {
      ...tableOptions,
      startY: tableStartY,
      margin: tableOptions.margin ?? ledgerMargins(this.layout),
      showHead: tableOptions.showHead ?? 'everyPage',
      rowPageBreak: tableOptions.rowPageBreak ?? (rowCount > 12 ? 'auto' : 'avoid'),
      pageBreak: tableOptions.pageBreak ?? 'auto',
    });

    this.cursorY = this.doc.lastAutoTable.finalY + afterGap;
    this.log(`section-table-complete:${sectionId}`, {
      sectionId,
      tableStartY: Number(tableStartY.toFixed(2)),
      finalY: Number(this.cursorY.toFixed(2)),
    });
    return this.cursorY;
  }

  drawTextLines(lines, {
    lineHeight = TABLE_METRICS.LINE_HEIGHT,
    fontSize = 7,
    fontStyle = 'normal',
    requiredHeight,
    pageBreakReason = 'text-block',
  } = {}) {
    const blockHeight = requiredHeight ?? lines.length * lineHeight + TABLE_METRICS.GAP_AFTER_TEXT;
    this.ensureSpace(blockHeight, pageBreakReason, { lineCount: lines.length });

    this.doc.setFont('helvetica', fontStyle);
    this.doc.setFontSize(fontSize);
    lines.forEach((line, i) => {
      this.doc.text(line, this.layout.SIDE, this.cursorY + i * lineHeight);
    });
    this.cursorY += blockHeight;
    return this.cursorY;
  }
}
